import { Divider, FormControlLabel, Grid, Radio, RadioGroup, TextField } from "@mui/material";
import { useCallback, useContext, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import ProposalContext from "./providers/ProposalContextProvider";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import useUpdateProposalDiscount from "hooks/proposals/useUpdateProposalDiscount";
import formatCentsToUSD from "utils/money/formatCentsToUSD";

type DiscountType = "percent" | "amount";

export default function ProposalDiscount({ onClose }: { onClose: () => void }) {
  const { proposal } = useContext(ProposalContext);
  const [mutation, { isSuccess }] = useUpdateProposalDiscount();

  const profitCents = +proposal.profit;
  const overallTotalCents = +proposal.overallTotal;
  const existingDiscountCents = +proposal.discount;

  const schema = useMemo(
    () =>
      z
        .object({
          discountType: z.enum(["percent", "amount"]),
          discountValue: z.string(),
        })
        .superRefine((data, ctx) => {
          const numericValue = parseFloat(data.discountValue);
          if (isNaN(numericValue) || numericValue <= 0) return;

          const calculatedDiscountCents =
            data.discountType === "percent"
              ? Math.round((numericValue / 100) * overallTotalCents)
              : Math.round(numericValue * 100);

          if (calculatedDiscountCents > profitCents) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Discount cannot exceed the profit amount",
              path: ["discountValue"],
            });
          }
        }),
    [profitCents, overallTotalCents]
  );

  type DiscountFormValues = z.infer<typeof schema>;

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<DiscountFormValues>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      discountType: existingDiscountCents > 0 ? "amount" : "percent",
      discountValue: existingDiscountCents > 0 ? (existingDiscountCents / 100).toFixed(2) : "",
    },
  });

  const discountType = watch("discountType");
  const discountValue = watch("discountValue");

  const discountCents = useMemo(() => {
    const numericValue = parseFloat(discountValue);
    if (isNaN(numericValue) || numericValue <= 0) return 0;

    if (discountType === "percent") {
      return Math.round((numericValue / 100) * overallTotalCents);
    }
    return Math.round(numericValue * 100);
  }, [discountType, discountValue, overallTotalCents]);

  const profitAfterDiscount = profitCents - discountCents;
  const proposalTotalAfterDiscount = overallTotalCents - discountCents;

  const onSubmit = useCallback(async () => {
    const result = await mutation({
      variables: {
        input: {
          id: proposal.id,
          discount: discountCents,
        },
      },
    });
    const success = isSuccess(result.data);
    if (success) {
      onClose();
    }
  }, [mutation, discountCents, proposal.id, isSuccess, onClose]);

  return (
    <MDBox px={3} pt={2} pb={2} component="form" onSubmit={handleSubmit(onSubmit)}>
      <MDTypography variant="subtitle2" fontWeight="medium" mb={1.5}>
        Enter Discount Type
      </MDTypography>

      <Controller
        name="discountType"
        control={control}
        render={({ field }) => (
          <RadioGroup
            row
            value={field.value}
            onChange={(e) => {
              field.onChange(e.target.value);
              setValue("discountValue", "");
            }}
          >
            <FormControlLabel value="percent" control={<Radio />} label="Percent" />
            <FormControlLabel value="amount" control={<Radio />} label="Amount" />
          </RadioGroup>
        )}
      />

      <MDBox mt={1.5} mb={2}>
        <Controller
          name="discountValue"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              type="number"
              label={discountType === "percent" ? "Discount (%)" : "Discount ($)"}
              inputProps={{
                min: 0,
                step: "0.01",
              }}
              error={!!errors.discountValue}
              helperText={errors.discountValue?.message || ""}
            />
          )}
        />
      </MDBox>

      <Divider />

      <MDBox mt={1.5} mb={1.5}>
        <Grid container spacing={0.5}>
          <Grid item xs={8}>
            <MDTypography variant="body2">Proposal Total before Discount:</MDTypography>
          </Grid>
          <Grid item xs={4}>
            <MDTypography variant="body2" fontWeight="medium" textAlign="right">
              {formatCentsToUSD(overallTotalCents)}
            </MDTypography>
          </Grid>

          <Grid item xs={8}>
            <MDTypography variant="body2">Profit Amount:</MDTypography>
          </Grid>
          <Grid item xs={4}>
            <MDTypography variant="body2" fontWeight="medium" textAlign="right">
              {formatCentsToUSD(profitCents)}
            </MDTypography>
          </Grid>

          <Grid item xs={8}>
            <MDTypography variant="body2">Discount Amount:</MDTypography>
          </Grid>
          <Grid item xs={4}>
            <MDTypography
              variant="body2"
              fontWeight="medium"
              textAlign="right"
              color={!!errors.discountValue ? "error" : "text"}
            >
              {formatCentsToUSD(discountCents)}
            </MDTypography>
          </Grid>

          <Grid item xs={8}>
            <MDTypography variant="body2">Profit after Discount:</MDTypography>
          </Grid>
          <Grid item xs={4}>
            <MDTypography
              variant="body2"
              fontWeight="medium"
              textAlign="right"
              color={profitAfterDiscount < 0 ? "error" : "text"}
            >
              {formatCentsToUSD(profitAfterDiscount)}
            </MDTypography>
          </Grid>

          <Grid item xs={8}>
            <MDTypography variant="body2">Proposal Total after Discount:</MDTypography>
          </Grid>
          <Grid item xs={4}>
            <MDTypography variant="body2" fontWeight="medium" textAlign="right">
              {formatCentsToUSD(proposalTotalAfterDiscount)}
            </MDTypography>
          </Grid>
        </Grid>
      </MDBox>

      <Divider />

      <MDBox mt={1.5} display="flex" justifyContent="flex-end">
        <MDButton color="success" type="submit" disabled={!!errors.discountValue}>
          Accept
        </MDButton>
      </MDBox>
    </MDBox>
  );
}
